import { ArrowForward, Error as ErrorIcon } from "@mui/icons-material";
import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import { DefaultOverlayPartialAppState, PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { Configuration, Project, ReportingItem } from "product-test-types";
import {
  CHARACTER_LIMITATIONS,
  findDropdownOption,
  getCustomFieldPublicId,
  getFieldValidation,
} from "product-test-utils";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../app";
import {
  getFormValuesFromEventPayload,
  PimoFormDialog,
} from "../components/pimo-form";
import { APP_ROUTES } from "../constants";
import {
  deleteAttachment,
  downloadAttachment,
  fetchAttachmentsByReportingItem,
  removeTemporaryAttachments,
  uploadAttachment,
} from "../helpers/fetch-attachments";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import {
  fetchProject,
  ProjectValidationErrors,
  updateProject,
} from "../helpers/fetch-project";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
import { toDropdownItems } from "../utils/dropdown-options";
import { nonNegativeBudgetRule } from "./non-negative-budget";
import { populateEmptyCustomFields } from "./populate-empty-custom-fields";

const STYLE_TEXT = {
  "& .MuiInputBase-input": { fontSize: "14px" },
  "& .MuiInputBase-inputMultiline, & textarea": { fontSize: "14px" },
  "& .MuiInputLabel-root": { fontSize: "14px" },
  fontSize: "14px",
} as const;

function slugifyTabId(value: string): string {
  return value.replace(/ /g, "-").toLowerCase();
}

function getOrderedMaturityNamesForItems(
  items: ReportingItem[],
  configuration: Configuration | undefined
): string[] {
  const configuredOrder = (configuration?.maturityConfiguration ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((e) => e.name)
    .filter((n): n is string => Boolean(n));

  const discovered = Array.from(
    new Set(items.map((item) => item.basicItem?.maturity || "No Maturity"))
  );

  return [
    ...configuredOrder.filter((n) => discovered.includes(n)),
    ...discovered.filter((n) => !configuredOrder.includes(n)),
  ];
}

function isValidationErrors(
  result: unknown
): result is ProjectValidationErrors {
  return (
    typeof result === "object" &&
    result !== null &&
    "validationErrors" in result
  );
}

export async function buildDimensionFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"id" | "viewname", TestAppState>
) {
  const dialog = app.createView({ name: "edit-subentity-by-dimension" });
  const editForm = new PimoFormDialog<Project>();
  const form = dialog.addComponent({ component: editForm });
  let incompleteAssessmentProject: Project | undefined;

  async function saveProject(
    submittedProject: Project | undefined,
    options: { allowIncompleteAssessment?: boolean } = {}
  ) {
    const language = app.getAppState().language || "en";
    if (!submittedProject?.id) return;

    if (options.allowIncompleteAssessment !== true) {
      const isComplete = await editForm.getFormMethods()?.areAllTabsValid?.();
      if (isComplete === false) {
        incompleteAssessmentProject = submittedProject;
        app.patchAppState({ showIncompleteDimensionAssessmentDialog: true });
        return;
      }
    }

    try {
      const result = await updateProject(
        submittedProject.id,
        submittedProject,
        true,
        options.allowIncompleteAssessment === true
      );

      if (isValidationErrors(result)) {
        const formMethods = editForm.getFormMethods();
        for (const [key, err] of Object.entries(result.validationErrors)) {
          formMethods?.setError(key as never, err);
        }
        await formMethods?.areAllTabsValid?.();
        return;
      }

      app.patchAppState({ currentReport: { project: result } });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(language, "allChangesSaved") ||
          "All changes have been saved.",
        "success"
      );
      closeEditForm();
    } catch {
      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(language, "settings.saveFailed") ||
          "Saving failed. Please try again.",
        "error"
      );
    }
  }

  function registerFormEvents() {
    form.on("submit", async ({ payload }) => {
      await saveProject(getFormValuesFromEventPayload<Project>(payload));
    });

    form.on("submit-without-changes", async ({ payload }) => {
      await saveProject(getFormValuesFromEventPayload<Project>(payload));
    });
    form.on("cancel", () => closeEditFormWithCleanup());
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", () => closeEditForm());
  }

  registerFormEvents();

  function closeEditForm() {
    const currentProject = app.getAppState().currentReport?.project;
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<TestAppState>>(
        "ResourceLockPlugin"
      );
    resourceLockPlugin?.unlock(`project-update-${currentProject?.id}`);
    incompleteAssessmentProject = undefined;
    app.patchAppState({
      showEditFormConfirmationDialog: false,
      showIncompleteDimensionAssessmentDialog: false,
    });
    app.navigate(
      generatePath(APP_ROUTES.report, {
        id: String(currentProject?.id),
        viewname: "dashboard",
      })
    );
  }

  function createIncompleteAssessmentDialog() {
    const confirmationDialog = dialog.addComponent({ component: PMPopUp });

    confirmationDialog.mapVisibility(
      ({ showIncompleteDimensionAssessmentDialog }) =>
        showIncompleteDimensionAssessmentDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(
            language,
            "dialog.title.saveIncompleteAssessment"
          ) || "Save incomplete assessment?",
        description:
          getLocalizedMessage(
            language,
            "dialog.message.saveIncompleteAssessment"
          ) ||
          "You haven't completed all activities in this dimension yet. You can save your current progress and continue later. The dimension will remain marked as In Progress until all required activities are completed.",
        maxWidth: "sm",
        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.continueEditing") ||
          "Continue editing",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.saveProgress") ||
          "Save progress",
        cancelButtonFireEvent: () =>
          app.patchAppState({ showIncompleteDimensionAssessmentDialog: false }),
        confirmationButtonFireEvent: async () => {
          const submittedProject = incompleteAssessmentProject;
          app.patchAppState({ showIncompleteDimensionAssessmentDialog: false });
          incompleteAssessmentProject = undefined;
          await saveProject(submittedProject, {
            allowIncompleteAssessment: true,
          });
        },
        onClose: () =>
          app.patchAppState({ showIncompleteDimensionAssessmentDialog: false }),
        slotProps: { backdrop: { sx: { backgroundColor: "transparent" } } },
      };
    });
  }

  function closeEditFormWithCleanup() {
    const currentProject = app.getAppState().currentReport?.project;
    const reportingItemIds =
      currentProject?.reportingItems
        ?.map((item) => item?.id)
        .filter((id): id is string => Boolean(id)) ?? [];

    closeEditForm();

    if (reportingItemIds.length > 0) {
      void Promise.allSettled(
        reportingItemIds.map((id) => removeTemporaryAttachments(id))
      );
    }
  }

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({ component: PMPopUp });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the form, these changes will be lost.",
        maxWidth: "sm",
        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",
        cancelButtonFireEvent: () =>
          app.patchAppState({ showEditFormConfirmationDialog: false }),
        confirmationButtonFireEvent: () => {
          app.patchAppState({ showEditFormConfirmationDialog: false });
          closeEditFormWithCleanup();
        },
        onClose: () =>
          app.patchAppState({ showEditFormConfirmationDialog: false }),
        slotProps: { backdrop: { sx: { backgroundColor: "transparent" } } },
      };
    });
  }

  const route = parentRoute.createChildRoute<"id" | "dimension">({
    path: APP_ROUTES.editReportByDimension,
    view: dialog,
    isOverlayChild: true,
  });

  createConfirmationDialog();
  createIncompleteAssessmentDialog();

  route.on("load", async ({ payload }) => {
    const id = payload?.parameters?.id;
    const rawDimension = payload?.parameters?.dimension;
    if (!id || !rawDimension) return;

    const dimension = decodeURIComponent(rawDimension);
    const currentProject = await fetchProject({ id });
    const configuration = await fetchEntityConfiguration();

    populateEmptyCustomFields(currentProject?.project, configuration);

    app.patchAppState({
      currentReport: currentProject,
      showEditFormConfirmationDialog: false,
      showIncompleteDimensionAssessmentDialog: false,
    });

    form.mapState((state) => {
      const project = state.currentReport?.project;
      const normalizedReportingItems = project?.reportingItems?.map((item) => ({
        ...item,
        degreeOfCompletion: findDropdownOption(
          configuration?.degreeOfCompletionDefinition,
          item.degreeOfCompletion
        )?.name,
        status: findDropdownOption(configuration?.statusDefinition, item.status)
          ?.name,
        answer: findDropdownOption(configuration?.answerDefinition, item.answer)
          ?.name,
        semaphoreStatus1: findDropdownOption(
          configuration?.statusDefinition,
          item.semaphoreStatus1
        )?.name,
        semaphoreStatus2: findDropdownOption(
          configuration?.statusDefinition,
          item.semaphoreStatus2
        )?.name,
        semaphoreStatus3: findDropdownOption(
          configuration?.statusDefinition,
          item.semaphoreStatus3
        )?.name,
      }));

      const itemsForDimension = (normalizedReportingItems ?? []).filter(
        (item) => (item.basicItem?.dimension || "No Dimension") === dimension
      );
      const maturityOrder = getOrderedMaturityNamesForItems(
        itemsForDimension,
        configuration
      );
      const firstMaturity = maturityOrder[0];
      const initialSelectedTabId = firstMaturity
        ? slugifyTabId(`maturity-${firstMaturity}`)
        : undefined;

      return {
        id: project?.id ?? "",
        ...project,
        reportingItems: normalizedReportingItems,
        // tells the backend to only validate items belonging to this dimension
        _reportDimensionFilter: dimension,
        title: dimension,
        validationTarget: "all-tabs",
        initialSelectedTabId,
        enableCloseButton: true,
        expandAllChildren: false,
        actions: {
          save: { visible: true, overrideValidationTarget: "none" },
          "save-and-back": { overrideValidationTarget: "current-tab" },
          "save-and-next": { overrideValidationTarget: "current-tab" },
        },
        customButtons: {
          right: [
            {
              defaultAction: "save-and-next",
              label:
                getLocalizedMessage(state.language || "en", "next") || "Next",
              endIcon: <ArrowForward />,
              variant: "text",
            },
            {
              id: "cancel",
              label:
                getLocalizedMessage(state.language || "en", "cancel") ||
                "Cancel",
              variant: "text",
              defaultAction: "cancel",
            },
            {
              defaultAction: "save",
              label:
                getLocalizedMessage(state.language || "en", "save") || "Save",
              variant: "contained",
              sx: { color: "white" },
            },
          ],
        },
      };
    });

    await editForm.buildDynamicTabsAsynchronously(async () => {
      const appState = app.getAppState();
      const language = appState.language;
      const configuration = await fetchEntityConfiguration();
      const fetchedData = await fetchProject({ id });

      populateEmptyCustomFields(fetchedData?.project, configuration);
      app.patchAppState({ currentReport: fetchedData });

      const allItems = fetchedData?.project?.reportingItems ?? [];
      const reportingItemIndexById = new Map(
        allItems.map((item, index) => [String(item.id), index] as const)
      );

      // Only the items belonging to this dimension
      const itemsForDimension = allItems.filter(
        (item) => (item.basicItem?.dimension || "No Dimension") === dimension
      );

      const addReportingItemFields = (
        tab: ReturnType<typeof editForm.addFormTab>,
        reportingItem: ReportingItem,
        showDivider = false
      ) => {
        if (showDivider) {
          tab.addLayoutElement({ type: "form-section-divider" });
        }

        const indexOfReportingItem =
          reportingItem.id != null
            ? reportingItemIndexById.get(String(reportingItem.id))
            : undefined;

        if (indexOfReportingItem == null) return;

        const reportingItemForValueMapping =
          allItems[indexOfReportingItem] ?? reportingItem;

        tab.addLayoutElement({
          type: "form-tab-title",
          label: `${
            reportingItem.basicItem?.name ??
            reportingItem.basicItem?.id?.toString()
          }`,
        });

        tab.addFormField({
          type: "dropdown",
          label: `${getLocalizedMessage(language, "status") || "Status"}${
            getFieldValidation(configuration, "status", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${indexOfReportingItem}].status`,
          sx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          menuItemsSx: STYLE_TEXT,
          required: getFieldValidation(configuration, "status", "itemReporting")
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find(
                  (f) =>
                    configuration.statusReportingLevel === "overall" &&
                    f.fieldName === "status" &&
                    f.enabled
                )
            ),
          items: toDropdownItems(configuration?.statusDefinition),
        });

        tab.addFormField({
          type: "dropdown",
          label: `Answer${
            getFieldValidation(configuration, "answer", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${indexOfReportingItem}].answer`,
          sx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          menuItemsSx: STYLE_TEXT,
          required: getFieldValidation(configuration, "answer", "itemReporting")
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "answer" && f.enabled)
            ),
          items: toDropdownItems(configuration?.answerDefinition),
        });

        configuration?.fieldsDefinition
          ?.filter((field) => field.purpose === "itemReporting")
          ?.find(
            (f) =>
              f.fieldName === "status" &&
              f.enabled &&
              configuration.statusReportingLevel === "category"
          ) &&
          configuration?.semaphoreStatusDefinition?.forEach((status, index) =>
            tab.addFormField({
              type: "dropdown",
              uiOptions: { fieldSize: { xs: 6 } },
              label: `Status ${status.name}${
                getFieldValidation(configuration, "status", "itemReporting")
                  ? "*"
                  : ""
              }`,
              name: `reportingItems.[${indexOfReportingItem}].semaphoreStatus${
                index + 1
              }`,
              required: getFieldValidation(
                configuration,
                "status",
                "itemReporting"
              )
                ? {
                    value: true,
                    message:
                      getLocalizedMessage(
                        language || "en",
                        "validationErrors.fieldRequired"
                      ) || "This field is required",
                  }
                : undefined,
              items: toDropdownItems(configuration?.statusDefinition),
            })
          );

        tab.addFormField({
          type: "dropdown",
          uiOptions: { fieldSize: { xs: 6 } },
          label: `${
            getLocalizedMessage(language, "degreeOfCompletion") ||
            "Degree of completion"
          }${
            getFieldValidation(configuration, "completion", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${indexOfReportingItem}].degreeOfCompletion`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          required: getFieldValidation(
            configuration,
            "completion",
            "itemReporting"
          )
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "completion" && f.enabled)
            ),
          items: toDropdownItems(configuration?.degreeOfCompletionDefinition),
        });

        tab.addFormField({
          type: "dropdown",
          uiOptions: { fieldSize: { xs: 6 } },
          label: `${getLocalizedMessage(language, "progress") || "Progress"}${
            getFieldValidation(configuration, "progress", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          required: getFieldValidation(
            configuration,
            "progress",
            "itemReporting"
          )
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "progress" && f.enabled)
            ),
          name: `reportingItems.[${indexOfReportingItem}].progress`,
          items: Array.from({ length: 21 }, (_, i) => i * 5).map((value) => ({
            value: String(value),
            label: `${value}%`,
          })),
        });

        tab.addFormField({
          type: "number",
          uiOptions: { fieldSize: { xs: 6 } },
          label: `${
            getLocalizedMessage(language, "spentBudget") || "Spent Budget"
          } (€)${
            getFieldValidation(configuration, "spentBudget", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          required: getFieldValidation(
            configuration,
            "spentBudget",
            "itemReporting"
          )
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "spentBudget" && f.enabled)
            ),
          name: `reportingItems.[${indexOfReportingItem}].spentBudget`,
          min: nonNegativeBudgetRule(language),
        });

        tab.addFormField({
          type: "rich-text",
          label: `${getLocalizedMessage(language, "comment")}${
            getFieldValidation(configuration, "comment", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          required: getFieldValidation(
            configuration,
            "comment",
            "itemReporting"
          )
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "comment" && f.enabled)
            ),
          multiline: true,
          name: `reportingItems.[${indexOfReportingItem}].comment`,
          maxLength: {
            value: CHARACTER_LIMITATIONS["basicItems.{idx}.comment"],
            message:
              getLocalizedMessage(
                language || "en",
                "validationErrors.maxLength",
                {
                  maxLength: CHARACTER_LIMITATIONS["basicItems.{idx}.comment"],
                }
              ) ||
              `Must be ${CHARACTER_LIMITATIONS["basicItems.{idx}.comment"]} characters or less`,
          },
        });

        for (const field of configuration?.customFields?.filter(
          (f) => f.purpose === "itemReporting" && f.enabled
        ) ?? []) {
          const indexOfCustomFieldValue =
            reportingItemForValueMapping.customFields?.findIndex(
              (cfv) =>
                getCustomFieldPublicId(cfv.customFieldDefinition) ===
                getCustomFieldPublicId(field)
            ) ?? -1;

          if (indexOfCustomFieldValue < 0) continue;

          tab.addFormField({
            type:
              field.type === "Select" ? "dropdown" : field.type.toLowerCase(),
            uiOptions: { fieldSize: { xs: 6 } },
            label: `${field.label}${field.mandatory ? "*" : ""}`,
            name: `reportingItems.[${indexOfReportingItem}].customFields.[${indexOfCustomFieldValue}].value`,
            sx: STYLE_TEXT,
            isVisible: () => Boolean(field.enabled),
            ...(field.type === "Select"
              ? { items: toDropdownItems(field.options) }
              : {}),
          });
        }

        const attachmentField = configuration?.fieldsDefinition?.find(
          (f) => f.fieldName === "attachments" && f.purpose === "itemReporting"
        );

        if (attachmentField?.enabled) {
          tab.addFormField({
            type: "attachment",
            label: getLocalizedMessage(language, "attachments"),
            name: `reportingItems.[${indexOfReportingItem}].attachments`,
            entityId: reportingItem.id!,
            onFetchAttachments: fetchAttachmentsByReportingItem,
            onUploadAttachment: uploadAttachment,
            onDeleteAttachment: deleteAttachment,
            onDownloadAttachment: downloadAttachment,
            uploadLabel: getLocalizedMessage(language, "uploadDocuments"),
            required:
              attachmentField.isMandatory === true
                ? {
                    value: true,
                    message: "At least one attachment is required",
                  }
                : undefined,
          });
        }
      };

      // One tab per maturity within the selected dimension
      const maturityOrder = getOrderedMaturityNamesForItems(
        itemsForDimension,
        configuration
      );

      for (const maturity of maturityOrder) {
        const itemsForMaturity = itemsForDimension.filter(
          (item) => (item.basicItem?.maturity || "No Maturity") === maturity
        );
        const maturityTab = editForm.addFormTab({
          label: maturity,
          id: slugifyTabId(`maturity-${maturity}`),
          validationIndicatorSlot: (
            <ErrorIcon sx={{ color: theme.palette.error.main }} />
          ),
        });

        for (const [index, reportingItem] of itemsForMaturity.entries()) {
          addReportingItemFields(maturityTab, reportingItem, index > 0);
        }
      }

      createConfirmationDialog();
      createIncompleteAssessmentDialog();
    });
  });
}
